/**
 * Standard Galactic Alphabet text (vanilla's `minecraft:alt` font, used by the
 * enchanting table): font/ascii_sga.png laid out like ascii.png (16x16 cells),
 * glyph widths measured from the bitmap. Plus vanilla's EnchantmentNames word
 * generator. Falls back to the plain font canvas until the sheet loads.
 */

import { JavaRandom } from '../game/enchant/table';

let img: HTMLImageElement | null = null;
const widths = new Uint8Array(256).fill(5);
let loading = false;

function ensure(): void {
  if (loading || typeof document === 'undefined') return;
  loading = true;
  const im = new Image();
  im.onload = () => {
    const c = document.createElement('canvas');
    c.width = im.width; c.height = im.height;
    const g = c.getContext('2d', { willReadFrequently: true })!;
    g.drawImage(im, 0, 0);
    const d = g.getImageData(0, 0, c.width, c.height).data;
    const cell = Math.floor(im.width / 16);
    for (let code = 0; code < 256; code++) {
      const cx = (code & 15) * cell, cy = (code >> 4) * cell;
      let right = -1;
      for (let x = 0; x < cell; x++) for (let y = 0; y < cell; y++) if (d[((cy + y) * c.width + cx + x) * 4 + 3] > 0) { right = x; break; }
      widths[code] = right < 0 ? 3 : right + 1;
    }
    widths[32] = 3;
    img = im;
  };
  im.src = 'mc/font/ascii_sga.png';
}

export const sgaReady = (): boolean => { ensure(); return img !== null; };

export function sgaWidth(text: string): number {
  ensure();
  let w = 0;
  for (const ch of text) w += widths[ch.charCodeAt(0) & 255] + 1;
  return w;
}

/** Draw `text` in SGA into a new 1x canvas tinted `color` (no shadow, like vanilla's table). */
export function sgaText(text: string, color: string): HTMLCanvasElement {
  ensure();
  const w = Math.max(1, sgaWidth(text));
  const out = document.createElement('canvas');
  out.width = w; out.height = 8;
  const g = out.getContext('2d')!;
  g.imageSmoothingEnabled = false;
  if (img) {
    const cell = Math.floor(img.width / 16);
    let x = 0;
    for (const ch of text) {
      const code = ch.charCodeAt(0) & 255;
      if (code !== 32) g.drawImage(img, (code & 15) * cell, (code >> 4) * cell, widths[code], cell, x, 0, widths[code], 8);
      x += widths[code] + 1;
    }
    g.globalCompositeOperation = 'source-in';
    g.fillStyle = color;
    g.fillRect(0, 0, w, 8);
  }
  return out;
}

const WORDS = ('the elder scrolls klaatu berata niktu xyzzy bless curse light darkness fire air earth water hot dry cold wet ' +
  'ignite snuff embiggen twist shorten stretch fiddle destroy imbue galvanize enchant free limited range of towards ' +
  'inside sphere cube self other ball mental physical grow shrink demon elemental spirit animal creature beast humanoid ' +
  'undead fresh stale phnglui mglwnafh cthulhu rlyeh wgahnagl fhtagnbaguette').split(' ');

/** vanilla EnchantmentNames: seeded once per frame with the enchant seed, then 3-4 words per slot, cut to `maxWidth`. */
export class EnchantNames {
  private r = new JavaRandom(0);
  initSeed(seed: number): void { this.r = new JavaRandom(seed); }
  next(maxWidth: number): string {
    const n = this.r.nextInt(2) + 3;
    const parts: string[] = [];
    for (let i = 0; i < n; i++) parts.push(WORDS[this.r.nextInt(WORDS.length)]);
    let s = parts.join(' ');
    while (s.length && sgaWidth(s) > maxWidth) s = s.slice(0, -1);
    return s;
  }
}
